// src/pages/admin/ManageNews.js - Complete News Management System
import React, { useState, useEffect } from 'react';
import { 
  FaPlus, FaEdit, FaTrash, FaEye, FaNewspaper, FaFilter, 
  FaSearch, FaImage, FaCalendar, FaUser, FaTags, FaCheckCircle,
  FaTimesCircle, FaExclamationTriangle
} from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ManageNews = () => {
  const [news, setNews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'Technology',
    image: '',
    author: 'Admin',
    status: 'draft'
  });
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/news/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setNews(data.news || []);
      } else {
        // Fallback to localStorage if API fails
        const savedNews = localStorage.getItem('globalNews');
        if (savedNews) {
          setNews(JSON.parse(savedNews));
        }
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      // Fallback to localStorage
      const savedNews = localStorage.getItem('globalNews');
      if (savedNews) {
        setNews(JSON.parse(savedNews));
      }
    } finally {
      setLoading(false);
    }
  };

  const saveToStorage = (newsData) => {
    localStorage.setItem('globalNews', JSON.stringify(newsData));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const token = localStorage.getItem('token');
      const url = editingId 
        ? `${API_URL}/api/news/${editingId}`
        : `${API_URL}/api/news`;

      const response = await fetch(url, {
        method: editingId ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert(editingId ? 'News updated successfully!' : 'News published successfully!');
        fetchNews();
        resetForm();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save news');
        
        // Fallback to localStorage
        handleLocalStorageSave();
      }
    } catch (error) {
      console.error('Error saving news:', error);
      setError('Failed to save news. Saving to local storage...');
      handleLocalStorageSave();
    }
  };

  const handleLocalStorageSave = () => {
    if (editingId) {
      const updated = news.map(item => 
        item.id === editingId ? { ...formData, id: editingId, updatedAt: new Date().toISOString() } : item
      );
      setNews(updated);
      saveToStorage(updated);
    } else {
      const newItem = {
        ...formData,
        id: Date.now(),
        createdAt: new Date().toISOString(),
        views: 0
      };
      const updated = [newItem, ...news];
      setNews(updated);
      saveToStorage(updated);
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData({ 
      title: '', 
      content: '', 
      category: 'Technology', 
      image: '', 
      author: 'Admin', 
      status: 'draft' 
    });
    setShowForm(false);
    setEditingId(null);
    setError('');
  };

  const handleEdit = (item) => {
    setFormData({
      title: item.title,
      content: item.content,
      category: item.category,
      image: item.image || '',
      author: item.author,
      status: item.status
    });
    setEditingId(item._id || item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this news article?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/news/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('News deleted successfully!');
        fetchNews();
      } else {
        // Fallback to localStorage
        const updated = news.filter(item => item.id !== id && item._id !== id);
        setNews(updated);
        saveToStorage(updated);
      }
    } catch (error) {
      console.error('Error deleting news:', error);
      // Fallback to localStorage
      const updated = news.filter(item => item.id !== id && item._id !== id);
      setNews(updated);
      saveToStorage(updated);
    }
  };

  const handleStatusToggle = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/news/${id}/toggle-status`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchNews();
      } else {
        // Fallback to localStorage
        const updated = news.map(item => 
          (item.id === id || item._id === id) 
            ? { ...item, status: item.status === 'published' ? 'draft' : 'published' } 
            : item
        );
        setNews(updated);
        saveToStorage(updated);
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      // Fallback to localStorage
      const updated = news.map(item => 
        (item.id === id || item._id === id) 
          ? { ...item, status: item.status === 'published' ? 'draft' : 'published' } 
          : item
      );
      setNews(updated);
      saveToStorage(updated);
    }
  };

  const filteredNews = news
    .filter(item => filter === 'all' ? true : item.status === filter)
    .filter(item => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        item.title.toLowerCase().includes(query) ||
        item.content.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query) ||
        item.author.toLowerCase().includes(query)
      );
    });

  const stats = {
    total: news.length,
    published: news.filter(n => n.status === 'published').length,
    draft: news.filter(n => n.status === 'draft').length,
    totalViews: news.reduce((sum, n) => sum + (n.views || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="spinner-lg mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading news...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white flex items-center gap-3 mb-2">
              <FaNewspaper className="text-blue-600" />
              Manage News
            </h1>
            <p className="text-gray-600 dark:text-gray-400">Create and manage news articles</p>
          </div>
          <button
            onClick={() => {
              setShowForm(!showForm);
              if (showForm) resetForm();
            }}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-purple-700 flex items-center gap-2 transition-all transform hover:scale-105 shadow-lg"
          >
            {showForm ? <FaTimesCircle /> : <FaPlus />}
            {showForm ? 'Cancel' : 'Add News'}
          </button>
        </div>

        {/* Statistics Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform">
            <FaNewspaper className="text-3xl mb-3 opacity-80" />
            <div className="text-sm mb-1 opacity-90">Total Articles</div>
            <div className="text-4xl font-bold">{stats.total}</div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform">
            <FaCheckCircle className="text-3xl mb-3 opacity-80" />
            <div className="text-sm mb-1 opacity-90">Published</div>
            <div className="text-4xl font-bold">{stats.published}</div>
          </div>

          <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform">
            <FaExclamationTriangle className="text-3xl mb-3 opacity-80" />
            <div className="text-sm mb-1 opacity-90">Drafts</div>
            <div className="text-4xl font-bold">{stats.draft}</div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform">
            <FaEye className="text-3xl mb-3 opacity-80" />
            <div className="text-sm mb-1 opacity-90">Total Views</div>
            <div className="text-4xl font-bold">{stats.totalViews}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mb-8">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <FaSearch className="inline mr-2" />
                Search News
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by title, content, category..."
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <FaFilter className="inline mr-2" />
                Filter by Status
              </label>
              <div className="flex gap-2">
                {['all', 'published', 'draft'].map(status => (
                  <button
                    key={status}
                    onClick={() => setFilter(status)}
                    className={`flex-1 px-4 py-2 rounded-lg capitalize font-semibold transition-all ${
                      filter === status 
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg' 
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {status} ({status === 'all' ? news.length : news.filter(n => n.status === status).length})
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
            Showing {filteredNews.length} of {news.length} articles
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
            <div className="flex items-center">
              <FaExclamationTriangle className="text-red-500 mr-3" />
              <p className="text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        )}

        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mb-8">
            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
              {editingId ? <FaEdit className="text-blue-600" /> : <FaPlus className="text-green-600" />}
              {editingId ? 'Edit News Article' : 'Add New Article'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-gray-700 dark:text-gray-300 mb-2 font-semibold">
                  Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-600"
                  placeholder="Enter news title"
                />
              </div>

              <div>
                <label className="block text-gray-700 dark:text-gray-300 mb-2 font-semibold">
                  Content *
                </label>
                <textarea
                  required
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white h-40 resize-none focus:ring-2 focus:ring-blue-600"
                  placeholder="Enter news content"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 dark:text-gray-300 mb-2 font-semibold">
                    <FaTags className="inline mr-2" />
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-600"
                  >
                    <option value="Technology">Technology</option>
                    <option value="Business">Business</option>
                    <option value="Innovation">Innovation</option>
                    <option value="Industry">Industry</option>
                    <option value="Company">Company News</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 dark:text-gray-300 mb-2 font-semibold">
                    <FaUser className="inline mr-2" />
                    Author
                  </label>
                  <input
                    type="text"
                    value={formData.author}
                    onChange={(e) => setFormData({...formData, author: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-600"
                    placeholder="Author name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 dark:text-gray-300 mb-2 font-semibold">
                  <FaImage className="inline mr-2" />
                  Image URL
                </label>
                <input
                  type="url"
                  value={formData.image}
                  onChange={(e) => setFormData({...formData, image: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-600"
                  placeholder="https://example.com/image.jpg"
                />
                {formData.image && (
                  <img 
                    src={formData.image} 
                    alt="Preview" 
                    className="mt-3 max-w-xs rounded-lg shadow-md"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
              </div>

              <div>
                <label className="block text-gray-700 dark:text-gray-300 mb-2 font-semibold">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-600"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-4 rounded-xl hover:from-blue-700 hover:to-purple-700 font-semibold transition-all transform hover:scale-105 shadow-lg"
                >
                  {editingId ? 'Update Article' : 'Publish Article'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-4 border-2 border-gray-300 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 font-semibold transition-colors text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* News List */}
        <div className="space-y-4">
          {filteredNews.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center">
              <FaNewspaper className="text-6xl text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                {searchQuery ? 'No news articles match your search.' : 'No news articles found. Create your first one!'}
              </p>
            </div>
          ) : (
            filteredNews.map(item => (
              <div key={item._id || item.id} className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="flex flex-col md:flex-row gap-6">
                  {item.image && (
                    <img 
                      src={item.image} 
                      alt={item.title}
                      className="w-full md:w-48 h-48 object-cover rounded-xl shadow-md"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row justify-between items-start gap-4 mb-3">
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{item.title}</h3>
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <span className="flex items-center gap-1 px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full font-semibold">
                            <FaTags className="text-xs" />
                            {item.category}
                          </span>
                          <span className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                            <FaUser className="text-xs" />
                            {item.author}
                          </span>
                          <span className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                            <FaEye className="text-xs" />
                            {item.views || 0} views
                          </span>
                          <span className={`flex items-center gap-1 px-3 py-1 rounded-full font-semibold ${
                            item.status === 'published' 
                              ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' 
                              : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                          }`}>
                            {item.status === 'published' ? <FaCheckCircle /> : <FaExclamationTriangle />}
                            {item.status}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-3 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900 text-blue-600 transition-colors"
                          title="Edit"
                        >
                          <FaEdit size={20} />
                        </button>
                        <button
                          onClick={() => handleDelete(item._id || item.id)}
                          className="p-3 rounded-lg hover:bg-red-100 dark:hover:bg-red-900 text-red-600 transition-colors"
                          title="Delete"
                        >
                          <FaTrash size={20} />
                        </button>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-2 leading-relaxed">{item.content}</p>
                    
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 pt-4 border-t dark:border-gray-700">
                      <span className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <FaCalendar />
                        {new Date(item.createdAt).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </span>
                      <button
                        onClick={() => handleStatusToggle(item._id || item.id)}
                        className={`px-6 py-2 rounded-lg text-sm font-semibold transition-all transform hover:scale-105 ${
                          item.status === 'published'
                            ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                            : 'bg-green-500 text-white hover:bg-green-600'
                        }`}
                      >
                        {item.status === 'published' ? 'Unpublish' : 'Publish Now'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageNews;
